import { Slot } from 'radix-ui'
import { cva, type VariantProps } from 'class-variance-authority'
import { LoaderCircle } from 'lucide-react'
import {
  Children,
  Fragment,
  forwardRef,
  isValidElement,
  type ButtonHTMLAttributes,
  type ReactNode
} from 'react'
import { cn } from '#lib/utils'
import { ROW_HOVER_CLASS } from '#lib/rowStates'

// 中性按钮按下（aria-pressed）与 IconButton 同一外观；翻页的当前页、播放列表的当前项（aria-current）也用它。
const PRESSED_CLASS =
  'aria-pressed:bg-button-selected aria-pressed:text-foreground aria-pressed:[&_svg]:text-foreground aria-[current=page]:bg-button-selected aria-[current=true]:bg-button-selected data-[state=open]:bg-button-selected'

// 列表、目录与导航里整行可点的按钮：选中与当前项（aria-pressed / aria-current / data-selected）用行选中底色。
const ROW_SELECTED_STATES =
  'aria-pressed:bg-button-selected aria-pressed:hover:bg-button-selected aria-pressed:[&_.text-muted-foreground]:text-foreground aria-[current=page]:bg-button-selected aria-[current=page]:hover:bg-button-selected aria-[current=page]:[&_.text-muted-foreground]:text-foreground aria-[current=true]:bg-button-selected aria-[current=true]:hover:bg-button-selected data-[selected=true]:bg-button-selected data-[selected=true]:hover:bg-button-selected'

// 当前导航项加位置标记；普通行选择仍由底色表示。
const ROW_CURRENT_MARKER =
  'relative aria-[current=page]:before:absolute aria-[current=page]:before:inset-y-1.5 aria-[current=page]:before:left-0 aria-[current=page]:before:w-[3px] aria-[current=page]:before:rounded-full aria-[current=page]:before:bg-primary'

export const buttonVariants = cva(
  'inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-button-neutral-strong text-button-neutral-strong-foreground hover:bg-button-neutral-strong-hover active:bg-button-neutral-strong-pressed',
        create:
          'bg-button-create-strong text-button-create-strong-foreground hover:bg-button-create-strong-hover active:bg-button-create-strong-pressed',
        secondary: `bg-button-neutral text-foreground hover:bg-button-neutral-hover active:bg-button-neutral-pressed [&_svg]:text-muted-foreground ${PRESSED_CLASS}`,
        createTint:
          'bg-button-create text-button-create-foreground hover:bg-button-create-hover active:bg-button-create-pressed',
        control: `border border-control-border bg-background font-normal text-foreground hover:border-foreground/35 ${PRESSED_CLASS}`,
        toggle:
          'border border-control-border bg-transparent text-foreground hover:bg-accent active:bg-accent aria-pressed:border-ai/60 aria-pressed:bg-ai/10 aria-pressed:text-button-create-foreground aria-pressed:[&_svg]:text-ai aria-pressed:hover:bg-ai/10 aria-pressed:active:bg-ai/10 aria-pressed:focus-visible:bg-ai/10',
        ghost: `text-muted-foreground hover:bg-button-ghost-hover hover:text-foreground active:bg-button-neutral-hover ${PRESSED_CLASS}`,
        subtle: `text-foreground hover:bg-button-ghost-hover active:bg-button-neutral-hover ${PRESSED_CLASS}`,
        add: 'bg-button-add font-normal text-muted-foreground hover:bg-button-ghost-hover hover:text-foreground active:bg-button-neutral-hover',
        row: `w-full justify-start text-left font-normal text-foreground ${ROW_HOVER_CLASS} ${ROW_SELECTED_STATES} ${ROW_CURRENT_MARKER}`,
        text: 'clickable-text text-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        link: 'link-text focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        floating:
          'bg-popover text-foreground shadow-md hover:bg-button-floating-hover hover:text-foreground',
        destructive:
          'bg-button-danger text-button-danger-foreground hover:bg-button-danger-hover active:bg-button-danger-pressed',
        destructiveAction:
          'bg-button-danger-strong text-button-danger-strong-foreground hover:bg-button-danger-strong-hover active:bg-button-danger-strong-pressed'
      },
      // 图标比文字大约 2px，由尺寸决定；图标自带 size-* 类时以图标为准。
      size: {
        compact: "h-5 gap-1 px-2 text-[10px] [&_svg:not([class*='size-'])]:size-3",
        xs: "h-[26px] gap-1 px-2 text-[11px] [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1.5 px-2.5 text-xs [&_svg:not([class*='size-'])]:size-3.5",
        default: "h-9 px-3 py-2 text-sm [&_svg:not([class*='size-'])]:size-4",
        dialog: "h-9 min-w-20 gap-1.5 px-4 text-sm [&_svg:not([class*='size-'])]:size-4",
        md: "h-10 gap-2 px-3 text-sm [&_svg:not([class*='size-'])]:size-4",
        // 内容决定高度：多行的列表行、可展开的分组标题。
        auto: "h-auto min-h-8 gap-2 whitespace-normal px-2 py-1.5 text-xs [&_svg:not([class*='size-'])]:size-3.5",
        // 写在一句话或一格文字里：沿用所在文字的字号，不加高度和内边距。
        inline:
          "h-auto gap-1 rounded-sm p-0 align-baseline text-[length:inherit] [&_svg:not([class*='size-'])]:size-[1em]"
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /** 处理中：开头的图标换成转圈，没有图标时在文字前加转圈；同时禁用按钮。 */
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { asChild = false, loading = false, className, variant, size, disabled, children, ...props },
  ref
) {
  const Comp = asChild ? Slot.Root : 'button'
  return (
    <Comp
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && !asChild ? withLoadingIcon(children) : children}
    </Comp>
  )
})

function withLoadingIcon(children: ReactNode): ReactNode {
  const items = Children.toArray(children)
  const spinner = <LoaderCircle key="loading" aria-hidden="true" className="animate-spin" />
  // 开头是图标组件或 svg 时替换它；文字或文字包裹元素开头时在前面加转圈。
  const [first, ...rest] = items
  const leadsWithIcon =
    isValidElement(first) &&
    first.type !== Fragment &&
    (typeof first.type !== 'string' || first.type === 'svg')
  return leadsWithIcon ? [spinner, ...rest] : [spinner, ...items]
}
